
import { AlertTriangle, TrendingUp, AlertCircle, ShieldAlert, Activity, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';

interface RiskItem {
    description: string;
    impactedNodeId?: string;
    impactedNodeName?: string;
    mitigation: string;
    level: 'high' | 'medium' | 'low';
}

interface RiskAnalysis {
    overallLevel: 'low' | 'medium' | 'high';
    summary: {
        delivery: number;
        scope: number;
        client_readiness: number;
        architectural: number;
        confidence: number;
    };
    topRisks: RiskItem[];
}

interface RiskOverviewPanelProps {
    risks: RiskAnalysis;
    userRole: 'consultant' | 'client';
}

export function RiskOverviewPanel({ risks, userRole }: RiskOverviewPanelProps) {
    if (userRole !== 'consultant') return null; // Defensive check
    if (risks.overallLevel === 'low' && risks.topRisks.length === 0) return null; // Don't show if empty/safe? Or show "All Good"?

    const getLevelColor = (level: string) => {
        switch (level) {
            case 'high': return 'text-red-600 bg-red-50 border-red-200';
            case 'medium': return 'text-amber-600 bg-amber-50 border-amber-200';
            case 'low': return 'text-blue-600 bg-blue-50 border-blue-200';
            default: return 'text-gray-600 bg-gray-50 border-gray-200';
        }
    };

    const getIcon = (level: string) => {
        switch (level) {
            case 'high': return <ShieldAlert className="size-5 text-red-600" />;
            case 'medium': return <AlertTriangle className="size-5 text-amber-600" />;
            default: return <Activity className="size-5 text-blue-600" />;
        }
    };

    return (
        <Card className="border-l-4 border-l-amber-500 bg-white shadow-sm mb-6">
            <CardHeader className="pb-3 border-b border-gray-100 bg-amber-50/20">
                <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${risks.overallLevel === 'high' ? 'bg-red-100' : 'bg-amber-100'}`}>
                            {getIcon(risks.overallLevel)}
                        </div>
                        <div>
                            <CardTitle className="text-lg text-gray-900">Engagement Risk Overview</CardTitle>
                            <CardDescription className="flex items-center gap-2">
                                Consultant-Only View 
                                <Badge variant="outline" className="text-[10px] h-5">PRIVATE</Badge>
                            </CardDescription>
                        </div>
                    </div>
                    <Badge className={`${getLevelColor(risks.overallLevel)} text-sm px-3 py-1 capitalize`}>
                        {risks.overallLevel} Risk
                    </Badge>
                </div>
                
                {/* Risk Counters */}
                <div className="flex gap-4 mt-4 text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                        <span className="font-bold text-gray-700">{risks.summary.delivery}</span> Delivery
                    </div>
                    <div className="flex items-center gap-1">
                        <span className="font-bold text-gray-700">{risks.summary.scope}</span> Scope
                    </div>
                    <div className="flex items-center gap-1">
                        <span className="font-bold text-gray-700">{risks.summary.architectural}</span> Architectural
                    </div>
                    <div className="flex items-center gap-1">
                        <span className="font-bold text-gray-700">{risks.summary.client_readiness}</span> Client Readiness
                    </div>
                </div>
            </CardHeader>
            <CardContent className="pt-4">
                <h4 className="text-xs font-bold text-gray-500 uppercase mb-3 flex items-center gap-2">
                    <TrendingUp className="size-3" />
                    Top Active Risks
                </h4>
                <div className="space-y-3">
                    {risks.topRisks.length > 0 ? (
                        risks.topRisks.map((risk, idx) => (
                            <div key={idx} className={`p-3 rounded-lg border flex gap-3 items-start ${getLevelColor(risk.level)} bg-opacity-30`}>
                                <div className="mt-0.5 shrink-0">
                                    {getIcon(risk.level)}
                                </div>
                                <div className="flex-1">
                                    <div className="font-medium text-sm text-gray-900">
                                        {risk.description}
                                    </div>
                                    {risk.impactedNodeName && (
                                        <div className="text-xs mt-1 text-gray-600 flex items-center gap-1">
                                            Impacts: <Badge variant="outline" className="bg-white/50 text-[10px] h-4 px-1">{risk.impactedNodeName}</Badge>
                                        </div>
                                    )}
                                    <div className="mt-2 text-xs font-semibold flex items-center gap-1 text-gray-700">
                                        <ArrowRight className="size-3" />
                                        Mitigation: {risk.mitigation}
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-sm text-gray-500 italic flex items-center gap-2">
                            <Activity className="size-4" />
                            No critical risks detected at this time.
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
